import { Link } from "react-router-dom";

import { VILLAINS, villainAnchor, type Villain } from "../caseload";
import { useLatestDownload } from "../useLatestDownload";
import { usePickedRandom } from "../usePickedRandom";

/**
 * The four cinematic stills the hero rotates between, with the film-still
 * caption that belongs to each. Exported so a test can assert the hero shows
 * one of them rather than pinning the page to whichever variant a stubbed
 * `Math.random` happened to return.
 */
export const HERO_VARIANTS = [
  { id: "vault", src: "/bear/hero-vault.webp", caption: "BIG BEAR — on watch, Vault 7" },
  { id: "eye", src: "/bear/hero-eye.webp", caption: "BIG BEAR — sees every bit" },
  { id: "swarm", src: "/bear/hero-swarm.webp", caption: "BIG BEAR vs THE SWARM — scene 04" },
  {
    id: "rooftop",
    src: "/bear/hero-rooftop.webp",
    caption: "BIG BEAR — the city sleeps, he doesn't",
  },
] as const;

export function Home() {
  const download = useLatestDownload();
  // Held for the life of the page, so a re-render (the session resolving, a
  // download arriving) never swaps the still out from under the reader.
  const hero = usePickedRandom(HERO_VARIANTS);

  return (
    <>
      <section className="hero hero--cine marketing__bleed">
        {/* Decorative: the still sets a mood, the copy over it carries the
            meaning. The scrim above it is what makes that copy legible. */}
        <img className="hero__still" src={hero.src} alt="" decoding="async" fetchPriority="high" />
        <div className="hero__scrim" aria-hidden="true" />

        <div className="hero__content bleed-inner">
          <p className="hero__kicker">Big Bear Backup</p>
          <h1>
            Encrypted, deduplicating backup
            <br />
            for Windows 11.
          </h1>
          <p className="hero__sub">
            To a local drive, a NAS or SMB share, or the cloud. Point it at a
            folder once and it keeps every version, without keeping every byte
            twice.
          </p>

          <div className="hero__cta">
            {download ? (
              <>
                <a className="button button--primary button--large" href={download.nsisUrl}>
                  Download for Windows — v{download.version}
                </a>
                <p className="muted hero__meta">
                  Free · 64-bit · <a href={download.msiUrl}>MSI also available</a> · Released{" "}
                  {download.date} ·{" "}
                  <Link to={`/releases/${download.version}`}>Release notes →</Link>
                </p>
                {download.status === "building" && (
                  <p className="muted hero__building" role="status">
                    v{download.version}'s installers are still building — the download above may
                    point at the previous release for a few more minutes.
                  </p>
                )}
              </>
            ) : (
              <Link className="button button--primary button--large" to="/download">
                Download for Windows
              </Link>
            )}
          </div>
        </div>

        <p className="hero__chip">{hero.caption}</p>
      </section>

      <section className="band band--split">
        <div className="band__text">
          <h2>You can always get your data back — with or without us.</h2>
          <p>
            The application is proprietary. <strong>The format is not.</strong> The
            specification is published under CC0 and is complete enough to write a
            restore tool from scratch, with no access to our source. That's not just
            a claim: an independent restorer that shares no code with the backup
            engine restores the same golden fixtures on every CI run, and CI fails
            if it ever gains a dependency on the engine that would let the two
            quietly drift together.
          </p>
          <p>
            <Link to="/spec">Read the format specification →</Link>
          </p>
        </div>
        <img
          className="band__image"
          src="/bear/band-remains.webp"
          alt="Big Bear standing on a heap of wrecked robots in a rainy alley, a rescued family photograph propped up against the wreckage."
          loading="lazy"
          decoding="async"
        />
      </section>

      <section className="how">
        <h2>How it works</h2>
        <ol className="how__steps">
          <li>
            <h3>Point it at a folder</h3>
            <p>
              Local drive, NAS/SMB share, an S3-compatible bucket — Backblaze
              B2, AWS, Wasabi, MinIO — or sign in to Google Drive, OneDrive or
              Dropbox.
            </p>
          </li>
          <li>
            <h3>It backs up incrementally</h3>
            <p>
              Content-defined chunking means an edit only touches the chunks near
              it — every run after the first is fast.
            </p>
          </li>
          <li>
            <h3>Old snapshots prune themselves</h3>
            <p>Retention runs on a schedule you set, and never removes the last copy of anything still in use.</p>
          </li>
        </ol>
      </section>

      <section className="villains marketing__bleed" aria-labelledby="caseload">
        <div className="villains__stakes">
          <div className="villains__stakes-inner bleed-inner">
            <h2 id="caseload">Big Bear's caseload</h2>
            <p>
              Somewhere in the future, your files are already gone — the drive
              died, the locks went on, the folder synced to nowhere. Big Bear
              exists so that day doesn't matter.
            </p>
          </div>
        </div>

        <div className="villain-grid bleed-inner">
          {VILLAINS.map((villain) => (
            <VillainCard key={villain.id} villain={villain} />
          ))}
        </div>
      </section>

      <section className="tiles-grid">
        <h2>What it does</h2>
        <div className="feature-tiles">
          <FeatureTile
            title="Deduplication"
            body="FastCDC content-defined chunking means identical data is only ever stored once, across every snapshot."
            anchor="/features#efficiency"
          />
          <FeatureTile
            title="End-to-end encryption"
            body="AES-256-GCM per blob, per-pack keys. Your passphrase wraps a random master secret — it's never derived from it."
            anchor="/features#security"
          />
          <FeatureTile
            title="Incremental, always"
            body="Falls out of the chunking, not a separate mode: every run after the first only stores what changed."
            anchor="/features#efficiency"
          />
          <FeatureTile
            title="Local, NAS, or cloud"
            body="A UNC path works anywhere a local one does. Cloud covers B2, AWS, Wasabi, MinIO and Ceph — or sign in to Google Drive, OneDrive or Dropbox."
            anchor="/features#storage"
          />
          <FeatureTile
            title="Retention that's actually safe"
            body="Independent, additive keep-rules, evaluated per job per machine. Pruning and reclaiming space are deliberately separate steps."
            anchor="/features#retention"
          />
          <FeatureTile
            title="Continuously verified"
            body="verify re-authenticates every byte, and every single-bit corruption is tested for in CI — never a silent partial result."
            anchor="/features#integrity"
          />
        </div>
      </section>

      <section className="band band--quiet">
        <h2>Worth knowing before you rely on it</h2>
        <p>
          <strong>Early development.</strong> The on-disk format is marked
          <code> DRAFT</code> and may change without a migration path until the
          first stable release. If that matters for your data today, wait for
          1.0 — or read{" "}
          <Link to="/spec">the format spec</Link>{" "}
          and judge for yourself.
        </p>
      </section>

      <section className="hero hero--footer hero--rooftop marketing__bleed">
        <div className="hero__content bleed-inner">
          <p className="hero__closing">The city sleeps. Big Bear doesn't.</p>
          {download && (
            <a className="button button--primary button--large" href={download.nsisUrl}>
              Download for Windows — v{download.version}
            </a>
          )}
        </div>
      </section>
    </>
  );
}

/** A case file: the still, who it is, what it really is, and the shipped
 * feature that answers it. The whole card is clickable — the link's `::after`
 * covers it — but only the link text names the destination, so the accessible
 * name stays short.
 *
 * The `id` is the other end of the round trip: `/features` credits each group
 * with the villains it defeats, and those credits link back to the individual
 * case file rather than the top of the caseload. */
function VillainCard({ villain }: { villain: Villain }) {
  return (
    <article className="villain" id={villainAnchor(villain.id)}>
      <img
        className="villain__image"
        src={villain.image}
        alt={villain.alt}
        loading="lazy"
        decoding="async"
      />
      <div className="villain__body">
        <p className="villain__threat">{villain.threat}</p>
        <h3 className="villain__name">{villain.name}</h3>
        <p className="villain__brief">{villain.brief}</p>
        <p className="villain__win">{villain.win}</p>
        <Link className="villain__link" to={villain.anchor}>
          How Big Bear wins<span className="visually-hidden"> against {villain.name}</span> →
        </Link>
      </div>
    </article>
  );
}

function FeatureTile({ title, body, anchor }: { title: string; body: string; anchor: string }) {
  return (
    <Link className="feature-tile" to={anchor}>
      <h3>{title}</h3>
      <p className="muted">{body}</p>
    </Link>
  );
}
